import reportsTokens, {
  COMPACT_QUERY,
  PHONE_QUERY,
  PHONE_SM_QUERY,
  reportsRecordListTokens,
} from "screens/Reports/Reports.tokens";

const { search, selectionBar } = reportsRecordListTokens;
/** Desktop toolbar values, traced from `Reports.dc.html`. */
const dt = reportsTokens.toolbar;

// Brand red fill (`inkfree.main` = #dd3824). White on it measures ~4.5:1 —
// meets 4.5:1 WCAG 1.4.3 for this 0.84375rem/700 label; `dark` (#A8320C)
// carries the hover step with even more headroom.
const DOWNLOAD_ENABLED_BG = dt.download.enabledBg;
const DOWNLOAD_ENABLED_HOVER_BG = dt.download.enabledHoverBg;
const DOWNLOAD_ENABLED_TEXT = dt.download.enabledColor;
const DOWNLOAD_DISABLED_BG = dt.download.disabledBg;
const DOWNLOAD_DISABLED_TEXT = dt.download.disabledColor;
const DOWNLOAD_DISABLED_BORDER = dt.download.disabledBorder;

/** Focus ring shared by the toolbar controls. */
const FOCUS_RING = "primary.main";

export const reportDownloadButtonSx = {
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  boxSizing: "border-box",
  height: dt.download.height,
  minHeight: dt.download.height,
  px: dt.download.paddingX,
  py: 0,
  borderRadius: dt.download.radius,
  border: "none",
  fontSize: dt.download.fontSize,
  fontWeight: dt.download.fontWeight,
  lineHeight: 1,
  letterSpacing: 0,
  textTransform: "none",
  whiteSpace: "nowrap",
  boxShadow: "none",
  transition: "all .12s",
  backgroundColor: DOWNLOAD_ENABLED_BG,
  color: DOWNLOAD_ENABLED_TEXT,
  "& .MuiButton-startIcon": { ml: 0, mr: dt.download.gap },
  "& .MuiButton-startIcon > *:nth-of-type(1)": {
    fontSize: dt.download.iconSize,
  },
  "&:hover": {
    border: "none",
    backgroundColor: DOWNLOAD_ENABLED_HOVER_BG,
    boxShadow: "none",
  },
  // `boxShadow: none` above also erases MUI's focus shadow, so the ring is
  // restated as an outline (WCAG 2.4.7).
  "&.Mui-focusVisible": {
    outline: "2px solid",
    outlineColor: FOCUS_RING,
    outlineOffset: "2px",
  },
  "&.Mui-disabled": {
    pointerEvents: "auto",
    cursor: "not-allowed",
    border: "1px solid",
    borderColor: DOWNLOAD_DISABLED_BORDER,
    backgroundColor: DOWNLOAD_DISABLED_BG,
    color: DOWNLOAD_DISABLED_TEXT,
    boxShadow: "none",
  },
};

/**
 * Search field shared by every Reports tab. Strips the outlined border so the
 * field reads as part of its wrapper, then puts back the two things that strip
 * costs: a focus indicator (2.4.7) and a placeholder dark enough to be read
 * (1.4.3 — MUI's default is the input colour at 0.42 opacity, ~2.62:1).
 *
 * Pair it with `reportSearchInputProps` so the field also carries an accessible
 * name: a placeholder is not a label (4.1.2 / 3.3.2).
 */
export const reportSearchFieldSx = {
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none !important",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none !important",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none !important",
  },
  "&.MuiOutlinedInput-root.Mui-focused": {
    border: "none !important",
  },
  // The wrapper now draws the box (border, radius, padding), so the input
  // itself contributes nothing but its text — `Reports.dc.html` runs the value
  // flush against the 11px icon gap.
  "& .MuiOutlinedInput-root": { padding: 0 },
  "& .MuiInputBase-input, & .MuiOutlinedInput-input": {
    padding: 0,
    height: "auto",
    fontSize: dt.search.fontSize,
    color: dt.search.color,
    lineHeight: 1.4,
  },
  // The wrapper carries the focus indicator via `:focus-within`; a second ring
  // on the input would double it up.
  "& .MuiOutlinedInput-root.Mui-focused": { outline: "none" },
  "& .MuiInputBase-input::placeholder": {
    color: dt.search.placeholder,
    opacity: 1,
  },
};

/* ── Phone / tablet toolbar ──────────────────────────────────────────────
 *
 * `Reports - Mobile & Tablet.dc.html` re-draws the toolbar below `md`: the
 * search field loses the desktop's 56px frame for a 40px pill, the date-range
 * control is dropped, and the Download button moves down beside "Select all"
 * at a smaller size. Everything from `md` up is unchanged.
 */

/**
 * Wrapper around the search `TextField`. The desktop values are the ones the
 * four report views used inline before this moved here, so `md` and up is
 * byte-for-byte what it was.
 */
export const reportSearchWrapperSx = {
  flexGrow: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: dt.search.gap,
  boxSizing: "border-box",
  p: dt.search.padding,
  bgcolor: dt.search.bg,
  borderRadius: dt.search.radius,
  border: "1px solid",
  borderColor: dt.search.border,
  transition: "border-color .12s",
  "&:focus-within": { borderColor: dt.search.focusBorder },
  // Everything the reference changes is expressed as a `max-width` override so
  // the desktop declarations above are the ones that survive at `md` and up.
  [COMPACT_QUERY]: {
    display: "flex",
    alignItems: "center",
    backgroundColor: search.bg,
    borderRadius: search.radius,
    borderColor: search.border,
    padding: search.padding.tablet,
  },
  [PHONE_QUERY]: { padding: search.padding.phone },
};

/**
 * The field itself. Below `md` the outlined input's own 16.5px padding comes
 * off — the wrapper draws the box now — and the type steps down with the
 * viewport.
 */
export const reportSearchFieldResponsiveSx = {
  ...reportSearchFieldSx,
  [COMPACT_QUERY]: {
    width: "100%",
    "& .MuiOutlinedInput-root": { padding: 0 },
    "& .MuiInputBase-input, & .MuiOutlinedInput-input": {
      padding: 0,
      fontSize: search.fontSize.tablet,
      lineHeight: 1.4,
    },
    "& .MuiOutlinedInput-root.Mui-focused": { outlineOffset: "6px" },
  },
  [PHONE_QUERY]: {
    "& .MuiInputBase-input, & .MuiOutlinedInput-input": {
      fontSize: search.fontSize.phone,
    },
  },
  [PHONE_SM_QUERY]: {
    "& .MuiInputBase-input, & .MuiOutlinedInput-input": {
      fontSize: search.fontSize.phoneSm,
    },
  },
};

/** Magnifier ahead of the input; 20px on the desktop, a step down below it. */
export const reportSearchAdornmentSx = {
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  // The wrapper's own 11px gap spaces the glyph from the value.
  mr: 0,
  color: dt.search.iconColor,
  "& img": { width: dt.search.iconSize, height: dt.search.iconSize },
  "& svg": { width: dt.search.iconSize, height: dt.search.iconSize },
  [COMPACT_QUERY]: {
    marginRight: search.gap,
    "& img": {
      width: search.iconSize.tablet,
      height: search.iconSize.tablet,
    },
  },
  [PHONE_QUERY]: {
    "& img": { width: search.iconSize.phone, height: search.iconSize.phone },
  },
};

/** The desktop Download button; the reference moves it below `md`. */
export const reportDownloadDesktopSx = {
  ...reportDownloadButtonSx,
  display: { xs: "none", md: "flex" } as const,
};

/** Its phone/tablet stand-in, in the selection bar under the search field. */
export const reportDownloadCompactSx = {
  ...reportDownloadButtonSx,
  height: selectionBar.downloadHeight,
  minHeight: selectionBar.downloadHeight,
  px: selectionBar.downloadPaddingX,
  borderRadius: selectionBar.downloadRadius,
  fontSize: selectionBar.downloadFontSize,
  "& .MuiButton-startIcon": { ml: 0, mr: selectionBar.downloadGap },
  "& .MuiButton-startIcon > *:nth-of-type(1)": {
    fontSize: selectionBar.downloadIconSize.tablet,
  },
  [PHONE_QUERY]: {
    "& .MuiButton-startIcon > *:nth-of-type(1)": {
      fontSize: selectionBar.downloadIconSize.phone,
    },
  },
  "&.Mui-disabled": {
    pointerEvents: "auto",
    cursor: "not-allowed",
    border: "none",
    backgroundColor: DOWNLOAD_DISABLED_BG,
    color: DOWNLOAD_DISABLED_TEXT,
    boxShadow: "none",
  },
};

/** Date-range control the reference drops below `md`. */
export const reportDateFilterHiddenSx = {
  display: { xs: "none", md: "flex" },
} as const;

/** Accessible name for a Reports search field; mirrors its visible placeholder. */
export const reportSearchInputProps = (label: string) => ({
  "aria-label": label,
});

export const getReportDownloadLabel = (selectedCount: number) =>
  selectedCount > 0 ? `Download Report (${selectedCount})` : "Download Report";
